import t from "@/lang/lang";
import { RentingFeeRes } from "@/services/backend/qpm/monitors/types/monitor-view";
import { useQpmMonitorRentingMutation } from "@/services/backend/qpm/monitors/api/monitor-api";
import { useDebouncedDirtyMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  EditableRentingFeeRow,
  EditableRentingFeesTable,
  hasInvalidRentingFeeRows,
  rentingFeeRowsEqual,
  sortedRentingFeeRows,
} from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/editable-renting-fees-table";

export type MonitorRentingComponentProps = {
  monitorId: string;
  renting?: Record<number, RentingFeeRes>;
};

type SetMonitorRentingRequest = {
  monitorId: string;
  fees: EditableRentingFeeRow[];
};

function toRows(
  renting?: Record<number, RentingFeeRes>,
): EditableRentingFeeRow[] {
  if (!renting) {
    return [];
  }
  return sortedRentingFeeRows(
    Object.values(renting).map((fee) => ({
      durationMonths: fee.durationMonths,
      monthly: fee.monthly,
    })),
  );
}

export function MonitorRentingComponent({
  monitorId,
  renting,
}: MonitorRentingComponentProps) {
  const [setRenting, saveState] = useQpmMonitorRentingMutation();

  const initialRows = useMemo(() => toRows(renting), [renting]);
  const [rows, setRows] = useState<EditableRentingFeeRow[]>(initialRows);

  useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  const request = useMemo<SetMonitorRentingRequest>(
    () => ({
      monitorId,
      fees: sortedRentingFeeRows(rows),
    }),
    [monitorId, rows],
  );

  const persistRenting = useCallback(
    (nextRequest: SetMonitorRentingRequest) =>
      setRenting({
        monitorId: nextRequest.monitorId,
        fees: nextRequest.fees,
      }),
    [setRenting],
  );

  const handleRowsChange = useCallback((nextRows: EditableRentingFeeRow[]) => {
    setRows(sortedRentingFeeRows(nextRows));
  }, []);

  useDebouncedDirtyMutationWithPersistenceStateContextUpdate(
    request,
    persistRenting,
    saveState.isLoading,
    saveState.error,
    saveState.isSuccess,
    saveState.reset,
    350,
    {
      toastError: true,
      skip: !monitorId || hasInvalidRentingFeeRows(rows),
      isDirty: (current, baseline) =>
        current.monitorId !== baseline.monitorId ||
        !rentingFeeRowsEqual(current.fees, baseline.fees),
    },
  );

  return (
    <div data-monitor-id={monitorId} className="flex flex-col gap-3">
      <div>
        <div className="text-base font-semibold">{t("Mietpreise für IoT")}</div>
        <div className="text-muted-foreground text-sm">
          {t("Diese Mietpreise werden als Mietposition berechnet.")}
        </div>
      </div>
      <EditableRentingFeesTable
        rows={rows}
        disabled={!monitorId}
        onChange={handleRowsChange}
        emptyText={t("Keine Mietpreise vorhanden")}
        addButtonText={t("Mietpreis hinzufügen")}
      />
    </div>
  );
}
